import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  useRef,
  useState,
} from 'react';

/**
 * Guards against the tap that mounted a thing being read as a tap *on* it.
 *
 * The mobile comments sheet is opened by a tap on the page underneath — placing
 * a pin, tapping a marker. The sheet and its backdrop therefore appear under the
 * finger, mid-gesture, and the click the browser dispatches after that pointerup
 * lands on whatever is now beneath it. If that happens to be a dismiss control,
 * the sheet shuts the instant it opened: the comment box flashes up and
 * vanishes. Returns false until enough time has passed that a tap cannot be the
 * one that caused the mount.
 */
export function useSettledAfterMount(ms = 350) {
  const mountedAt = useRef(Date.now());
  return () => Date.now() - mountedAt.current >= ms;
}

/** Dismiss layer behind the mobile comments sheet — tap the page to put it away. */
export function SheetBackdrop({ onDismiss }: { onDismiss: () => void }) {
  const settled = useSettledAfterMount();
  return (
    <div
      aria-hidden
      className="fixed inset-0 z-[35] bg-black/10 md:hidden"
      // pointerdown, not click: the click from the opening tap is already in
      // flight by the time this mounts, but its pointerdown is long gone.
      onPointerDown={() => {
        if (settled()) onDismiss();
      }}
    />
  );
}

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger' | 'success';
}) {
  const styles = {
    primary:
      'bg-neutral-900 text-white hover:bg-neutral-700 disabled:opacity-50',
    ghost:
      'bg-transparent text-neutral-700 hover:bg-neutral-100',
    danger: 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50',
  }[variant];
  return (
    <button
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${styles} ${className}`}
      {...props}
    />
  );
}

const INPUT_CLASS =
  'w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900';

export function Input({
  type,
  className = '',
  wrapperClassName = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { wrapperClassName?: string }) {
  const [reveal, setReveal] = useState(false);

  // Password fields get a show/hide toggle so users can check what they typed.
  if (type === 'password') {
    return (
      <div className={`relative ${wrapperClassName}`}>
        <input type={reveal ? 'text' : 'password'} className={`${INPUT_CLASS} pr-10 ${className}`} {...props} />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setReveal((r) => !r)}
          title={reveal ? 'Hide password' : 'Show password'}
          aria-label={reveal ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 hover:text-neutral-700"
        >
          <EyeIcon off={reveal} />
        </button>
      </div>
    );
  }
  return <input type={type} className={`${INPUT_CLASS} ${className}`} {...props} />;
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {off ? (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a3 3 0 0 0 4.2 4.2" />
          <path d="M9.9 4.2A10.9 10.9 0 0 1 12 4c6.5 0 10 8 10 8a13.4 13.4 0 0 1-2.16 2.94M6.1 6.1A13.3 13.3 0 0 0 2 12s3.5 8 10 8a10.9 10.9 0 0 0 3.4-.55" />
        </>
      ) : (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-neutral-700">{label}</span>
      {children}
    </label>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-xl border border-neutral-200 bg-white shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="whitespace-pre-line text-sm text-red-600">{children}</p>
  );
}

export function Spinner() {
  return (
    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" />
  );
}
